// Teacher Availability Tab - SAT V4 Zone
import { DsCard, DsTypography } from '@ice-web-app/designSystem';
import { Box, Chip, Grid, Stack } from '@mui/material';
import type { FC } from 'react';
import { useState } from 'react';
import type { Teacher, Availability } from '../../../../types';
import { TeacherTimetableGrid, WeekToggle } from '../../../../lib/components';
import type { WeekType } from '../../../../lib/components';
import { useSchoolStructureStore, useSettingsStore } from '../../../../stores';
import { getTeacherScheduledLessons, getTeacherDuties } from '../../../../lib/mockData';

interface AvailabilityTabProps {
  teacher: Teacher;
  onUpdate?: (updates: Partial<Teacher>) => void;
}

const AvailabilityTab: FC<AvailabilityTabProps> = ({ teacher, onUpdate }) => {
  const { periods } = useSchoolStructureStore();
  const { scheduleType } = useSettingsStore();
  const [availability] = useState<Availability>(teacher.availability);
  const [selectedWeek, setSelectedWeek] = useState<WeekType>('A');

  // Note: onUpdate can be used in the future for editing availability
  void onUpdate;

  // Get scheduled lessons and duties for this teacher from mock data
  const scheduledLessons = getTeacherScheduledLessons(teacher.id);
  const duties = getTeacherDuties(teacher.id);

  // Calculate availability stats
  const lessonPeriods = periods.filter((p) => p.type === 'lesson');
  const totalSlots = lessonPeriods.length * 5; // 5 weekdays
  let availableCount = 0;
  let preferredCount = 0;
  let unavailableCount = 0;
  let teachingCount = 0;

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
  days.forEach((day) => {
    lessonPeriods.forEach((period) => {
      // Check if there's a scheduled lesson for this slot
      const hasLesson = scheduledLessons.some((lesson) => {
        const matchesDay = lesson.dayOfWeek === day;
        const matchesPeriod = lesson.periodId === period.id;
        const matchesWeek = !selectedWeek || !lesson.week || lesson.week === selectedWeek;
        return matchesDay && matchesPeriod && matchesWeek;
      });

      if (hasLesson) {
        teachingCount++;
      } else {
        const status = availability[day]?.[period.id] || 'available';
        if (status === 'available') availableCount++;
        else if (status === 'preferred') preferredCount++;
        else unavailableCount++;
      }
    });
  });

  return (
    <Stack spacing={3}>
      {/* Availability Summary */}
      <Grid container spacing={2}>
        <Grid item xs={6} sm={3}>
          <DsCard sx={{ p: 2, textAlign: 'center', borderTop: 4, borderColor: 'success.main' }}>
            <DsTypography variant="h4" fontWeight={700} color="success.main">
              {availableCount}
            </DsTypography>
            <DsTypography variant="caption" color="text.secondary">
              Available Slots
            </DsTypography>
          </DsCard>
        </Grid>
        <Grid item xs={6} sm={3}>
          <DsCard sx={{ p: 2, textAlign: 'center', borderTop: 4, borderColor: 'info.main' }}>
            <DsTypography variant="h4" fontWeight={700} color="info.main">
              {preferredCount}
            </DsTypography>
            <DsTypography variant="caption" color="text.secondary">
              Preferred Slots
            </DsTypography>
          </DsCard>
        </Grid>
        <Grid item xs={6} sm={3}>
          <DsCard sx={{ p: 2, textAlign: 'center', borderTop: 4, borderColor: 'secondary.main' }}>
            <DsTypography variant="h4" fontWeight={700} color="secondary.main">
              {teachingCount}
            </DsTypography>
            <DsTypography variant="caption" color="text.secondary">
              Teaching Slots
            </DsTypography>
          </DsCard>
        </Grid>
        <Grid item xs={6} sm={3}>
          <DsCard sx={{ p: 2, textAlign: 'center', borderTop: 4, borderColor: 'error.main' }}>
            <DsTypography variant="h4" fontWeight={700} color="error.main">
              {unavailableCount}
            </DsTypography>
            <DsTypography variant="caption" color="text.secondary">
              Unavailable Slots
            </DsTypography>
          </DsCard>
        </Grid>
      </Grid>

      {/* Utilization Info */}
      <DsCard sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" flexWrap="wrap" useFlexGap>
          <DsTypography variant="body2">
            <strong>{Math.round(((availableCount + preferredCount) / totalSlots) * 100)}%</strong> availability rate
          </DsTypography>
          <DsTypography variant="body2" color="text.secondary">•</DsTypography>
          <DsTypography variant="body2">
            <strong>{teacher.currentPeriodsAssigned}</strong> periods currently assigned
          </DsTypography>
          <DsTypography variant="body2" color="text.secondary">•</DsTypography>
          <DsTypography variant="body2">
            <strong>{teacher.maxPeriodsPerWeek}</strong> max periods/week
          </DsTypography>
        </Stack>
      </DsCard>

      {/* Schedule Structure Info */}
      <DsCard sx={{ p: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
          <Box>
            <DsTypography variant="h6" fontWeight={600}>
              Teacher Availability Timetable
            </DsTypography>
            <DsTypography variant="body2" color="text.secondary">
              {scheduleType === 'Weekly' ? 'Same schedule repeats every week' : 'Alternating schedule between Week A and Week B'}
            </DsTypography>
          </Box>
          <Stack direction="row" spacing={2} alignItems="center">
            <Chip
              label={scheduleType}
              color={scheduleType === 'Bi-Weekly' ? 'primary' : 'default'}
              variant="outlined"
            />
            <Chip
              label={onUpdate ? 'Edit Mode' : 'View Only'}
              size="small"
              color={onUpdate ? 'primary' : 'default'}
            />
          </Stack>
        </Stack>
        {scheduleType === 'Bi-Weekly' && (
          <Box sx={{ mt: 2 }}>
            <WeekToggle selectedWeek={selectedWeek} onChange={setSelectedWeek} />
          </Box>
        )}
      </DsCard>

      {/* Timetable Grid */}
      <TeacherTimetableGrid
        periods={periods}
        availability={availability}
        scheduledLessons={scheduledLessons}
        duties={duties}
        selectedWeek={scheduleType === 'Bi-Weekly' ? selectedWeek : undefined}
        showWeekends={true}
      />

      {/* Notes */}
      <DsCard sx={{ p: 2, bgcolor: 'info.light' }}>
        <DsTypography variant="body2">
          <strong>Note:</strong> Teaching slots show scheduled lessons. Duty slots indicate session responsibilities.
          Other duties (SEND, Behavior, etc.) are shown in purple. Available slots have no assignments.
        </DsTypography>
      </DsCard>
    </Stack>
  );
};

export default AvailabilityTab;
